import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { fmtDateUS } from '../utils/dates';
import { withForm } from '../utils/wizardForm';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import IrsMaintenanceBanner from "./IrsMaintenanceBanner";
// patch-mode — только изменённые фрагменты

// imports — без изменений

export default function BeginAmendedReturn() {
  const { id: companyId } = useParams();  // company ID
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [subs, setSubs] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [error, setError] = useState('');
  // Тип может прийти из карточки на странице Tax & Form (?type=TGW|VIN|Suspension|Mileage)
  const typeFromUrl = searchParams.get('type');
  const [amendType, setAmendType] = useState(
    ['TGW', 'VIN', 'Suspension', 'Mileage'].includes(typeFromUrl) ? typeFromUrl : ''
  );

  useEffect(() => {
    async function loadSubs() {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `/api/companies/${companyId}/previous-submissions/`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!res.ok) throw new Error('Failed to load submissions');
        const data = await res.json();
        setSubs(data);
      } catch (e) {
        console.error(e);
      }
    }
    loadSubs();
  }, [companyId]);

  const handleContinue = () => {
    if (!selectedRecordId) {
      setError('Please select a submission to amend.');
      return;
    }
    if (!amendType) { // ↑ добавлено: проверка выбранного типа
      setError('Please select amendment type.');
      return;
    }
    const choice = subs.find(s => String(s.id) === selectedRecordId);
    if (!choice) {
      setError('Invalid selection.');
      return;
    }
    // Контекст поправки едет в адресе страницы, а не в localStorage: тот один на
    // все вкладки, и две поправки, открытые параллельно, ссылались бы на чужую
    // исходную декларацию (см. utils/wizardForm.js).

    // navigate to your amended‐return page
  const nextPath =
    amendType === 'VIN'
     ? `/company/${companyId}/file-form/change-vin`
     : amendType === 'Suspension'
       ? `/company/${companyId}/file-form/change-suspension`
       : amendType === 'Mileage'
         ? `/company/${companyId}/file-form/change-mileage`
         : `/company/${companyId}/file-form/change-vehicle-category`;
    navigate(withForm(nextPath, choice.form2290, {
      sid: choice.submission_id,
      date: new Date(choice.submitted_at).toISOString(),
    }));
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#E4F2C5E]">
      <HeaderLogged />
      <main className="flex-1 py-6 px-4">
        <div className="max-w-xl mx-auto bg-white shadow-md p-6 rounded-lg">
          <h1 className="text-2xl font-semibold text-[#0F2C5E] mb-4">
            Amended Return
          </h1>
          <p className="mb-4 text-gray-700">
            Select the prior submission you want to amend:
          </p>

          <div className="mb-4">
            <label htmlFor="prevSub" className="block font-medium mb-1">
              Previous Submission
            </label>
            <select
              id="prevSub"
              value={selectedRecordId}
              onChange={e => {
                setSelectedRecordId(e.target.value);
                setError('');
              }}
              className="w-full border p-2 focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
            >
              <option value="">— choose one —</option>
              {subs.map(s => (
                <option key={s.id} value={s.id}>
                  {s.form2290}-{s.tax_period || '—'} — {fmtDateUS(s.submitted_at)}
                </option>
              ))}
            </select>
          </div>

          {/* ↑ добавлено: выбор типа изменения */}
          <div className="mb-4">
            <span className="block font-medium mb-1">Amendment Type</span>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="amendType"
                  value="TGW"
                  checked={amendType === 'TGW'}
                  onChange={e => {
                    setAmendType(e.target.value);
                    setError('');
                  }}
                  className="h-4 w-4 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
                />
                <span>Change Vehicle Category (TGW)</span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="amendType"
                  value="VIN"
                  checked={amendType === 'VIN'}
                  onChange={e => {
                    setAmendType(e.target.value);
                    setError('');
                  }}
                  className="h-4 w-4 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
                />
                <span>Change VIN (VIN Correction)</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="amendType"
                  value="Suspension"
                  checked={amendType === 'Suspension'}
                  onChange={e => {
                    setAmendType(e.target.value);
                    setError('');
                  }}
                  className="h-4 w-4 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
                />
                <span>Mark Vehicles as Suspended</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="amendType"
                  value="Mileage"
                  checked={amendType === 'Mileage'}
                  onChange={e => {
                    setAmendType(e.target.value);
                    setError('');
                  }}
                  className="h-4 w-4 text-[#0F2C5E] border-gray-300 focus:ring-[#0F2C5E]"
                />
                <span>Suspended Vehicle Exceeded Mileage Limit</span>
              </label>
            </div>
          </div>

          {error && (
            <p className="text-red-600 text-sm mb-4">{error}</p>
          )}

          <button
            onClick={handleContinue}
            className="w-full bg-[#0F2C5E] text-white py-2 rounded hover:bg-[#0F2C5E]/80 transition"
          >
            Continue
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
